<template>
    <div class="checkout__paypal-tab" :class="{ apple_pay_active: applePayAvailable && paymentType == 'now' }">
        <div id="paypal-button"></div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import braintree from 'braintree-web';
import axios from 'axios';
import msg from '../../../messages';
import Tracker from '../../tracking/tracker';
import { createToken } from '../../csrf';

export default {
    props: {
        braintreeClientInstance: {
            required: true,
        },
        createPaymentMethodEndpoint: {
            default: 'create-payment-method',
            type: String,
        },
        createPaymentMethodFormData: {
            default() {
                return {};
            },
            type: Object,
        },
    },

    watch: {
        braintreeClientInstance: 'setUp',
    },

    computed: {
        ...mapState(['braintreeEnvironment', 'applePayAvailable', 'paymentType', 'domainSlug', 'paypalClientId']),
        ...mapGetters(['formData']),
    },

    methods: {
        setUp() {
            const vm = this;

            logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalSetupStarted });

            // Create a PayPal Checkout component.
            braintree.paypalCheckout
                .create({
                    client: this.braintreeClientInstance,
                })
                .then(function (paypalCheckoutInstance) {
                    logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalLoadSdkStarted });

                    // Load PayPals SDK
                    return paypalCheckoutInstance.loadPayPalSDK({
                        vault: true,
                    });
                })
                .then(function (paypalCheckoutInstance) {
                    logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalLoadSdkCompleted });

                    paypal
                        .Buttons({
                            fundingSource: paypal.FUNDING.PAYPAL,
                            locale: 'en_US',
                            style: {
                                layout: 'horizontal',
                                size: 'responsive',
                                color: 'silver',
                                shape: 'rect',
                                label: 'paypal',
                                tagline: 'false',
                                fundingicons: 'false',
                            },

                            createBillingAgreement: function () {
                                logger.addCheckoutBreadcrumb({
                                    message: msg.checkout.paypalCreateBillingAgreementStarted,
                                });

                                let commitType =
                                    vm.createPaymentMethodEndpoint === 'update-payment-method-create'
                                        ? 'increaseUpdatePaymentMethodAttempts'
                                        : 'increaseCheckoutPaymentAttempts';
                                vm.$store.commit(commitType);

                                window.ipse.dispatch({
                                    action: 'submit_payment_clicked',
                                    data_event: {
                                        type: 'paypal',
                                    },
                                });

                                vm.tracker.fireAddPaymentInfoEvent('Paypal');
                                return paypalCheckoutInstance.createPayment({
                                    flow: 'vault',
                                    intent: 'authorize',
                                    displayName:
                                        vm.domainSlug === 'ips' ? 'iPhone Photography School' : 'Photography Pro',
                                    locale: 'en_US',
                                    enableShippingAddress: false,
                                    landingPageType: 'login',
                                });
                            },

                            onApprove: function (data, actions) {
                                logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalPaymentApproved });
                                vm.$store.commit('setProcessingCheckout', true);
                                return paypalCheckoutInstance
                                    .tokenizePayment(data)
                                    .then(function (payload) {
                                        vm.createPaymentMethod(payload.nonce)
                                            .then((response) => {
                                                if (response.data.subscriptions) {
                                                    vm.$store.dispatch('updatePaymentMethod', {
                                                        responseData: response.data,
                                                    });
                                                } else {
                                                    vm.$store.commit('setLastCheckoutPaymentMethod', 'paypal');
                                                    vm.$emit('received-token', response.data.token);
                                                }
                                                vm.$store.commit('setPaymentMethod', {
                                                    paymentMethodToken: response.data.token,
                                                    defaultPaymentMethod: 'paypal',
                                                });
                                            })
                                            .catch((err) => {
                                                let type =
                                                    vm.createPaymentMethodEndpoint === 'update-payment-method-create'
                                                        ? 'update_payment_method'
                                                        : 'checkout';

                                                window.ipse.dispatch({
                                                    action: 'create_payment_method_failed',
                                                    data_event: {
                                                        purchase_flow: type,
                                                        payment_method: 'paypal',
                                                        error: err,
                                                    },
                                                });

                                                vm.$store.dispatch('showPaymentErrorModal', 'paypal');
                                                logger.captureException(
                                                    new Error(msg.checkout.paypalPaymentMethodFailed),
                                                    { extra: err },
                                                );
                                            });
                                    })
                                    .catch((err) => {
                                        let type =
                                            vm.createPaymentMethodEndpoint === 'update-payment-method-create'
                                                ? 'update_payment_method'
                                                : 'checkout';

                                        window.ipse.dispatch({
                                            action: 'create_payment_method_failed',
                                            data_event: {
                                                purchase_flow: type,
                                                payment_method: 'paypal',
                                                error: err,
                                            },
                                        });

                                        vm.$store.dispatch('showPaymentErrorModal', 'paypal');
                                        logger.captureException(new Error(msg.checkout.paypalTokenizeFailed), {
                                            extra: err,
                                        });
                                    });
                            },

                            onCancel: function () {
                                logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalCancelClicked });
                            },

                            onError: function (err) {
                                let type =
                                    vm.createPaymentMethodEndpoint === 'update-payment-method-create'
                                        ? 'update_payment_method'
                                        : 'checkout';

                                window.ipse.dispatch({
                                    action: 'purchase_failed',
                                    data_event: {
                                        purchase_flow: type,
                                        payment_method: 'paypal',
                                        error: err,
                                    },
                                });

                                vm.$store.dispatch('showPaymentErrorModal', 'paypal');
                                logger.captureException(new Error(msg.checkout.paypalError), { extra: err });
                            },
                        })
                        .render('#paypal-button');
                })
                .then(function () {
                    logger.addCheckoutBreadcrumb({ message: msg.checkout.paypalSetupCompleted });
                })
                .catch(function (err) {
                    logger.captureException(msg.checkout.paypalCheckoutComponentFailed, { extra: err });
                });
        },
        async createPaymentMethod(nonce) {
            const formData =
                Object.keys(this.createPaymentMethodFormData).length === 0
                    ? this.formData
                    : this.createPaymentMethodFormData;

            let tokenResponse = null;

            if (this.createPaymentMethodEndpoint === 'update-payment-method-create') {
                tokenResponse = await createToken('update-payment-method-create', '.checkout__content');
            }

            return axios.post(
                '/api/' + this.createPaymentMethodEndpoint,
                Object.assign({}, formData, {
                    nonce,
                    csrf_token: tokenResponse?.csrf_token,
                    recaptcha_token: tokenResponse?.recaptcha_token,
                }),
                {
                    headers: {
                        'x-transaction-id': logger.getTransactionId(),
                    },
                },
            );
        },
    },
};
</script>
